import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { Howl } from 'howler';
import { startBackgroundSession, stopBackgroundSession } from '../services/backgroundSession';

const ZenContext = createContext(null);

export const useZen = () => {
  const context = useContext(ZenContext);
  if (!context) {
    throw new Error('useZen must be used within a ZenProvider');
  }
  return context;
};

export const ZenProvider = ({ children }) => {
  // Main audio player state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [mainVolume, setMainVolume] = useState(0.7);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  
  // Ambient sounds state
  const [activeAmbientSounds, setActiveAmbientSounds] = useState({});
  const [ambientVolumes, setAmbientVolumes] = useState({});
  
  // Master volume
  const [masterVolume, setMasterVolume] = useState(0.8);
  
  // Refs for audio objects
  const mainAudioRef = useRef(null);
  const ambientSoundsRef = useRef({});
  const timeUpdateInterval = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mainAudioRef.current) {
        mainAudioRef.current.unload();
      }
      Object.values(ambientSoundsRef.current).forEach(sound => {
        if (sound) sound.unload();
      });
      if (timeUpdateInterval.current) {
        clearInterval(timeUpdateInterval.current);
      }
    };
  }, []);

  // Play main track
  const playTrack = useCallback((track) => {
    // Stop current track if playing
    if (mainAudioRef.current) {
      mainAudioRef.current.stop();
      mainAudioRef.current.unload();
      if (timeUpdateInterval.current) {
        clearInterval(timeUpdateInterval.current);
      }
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const audioUrl = track.file_url.startsWith('http') 
      ? track.file_url 
      : `${backendUrl}${track.file_url}`;

    mainAudioRef.current = new Howl({
      src: [audioUrl],
      html5: true,
      volume: mainVolume * masterVolume,
      onload: () => {
        setDuration(mainAudioRef.current.duration());
      },
      onplay: () => {
        setIsPlaying(true);
        // Start time update interval
        timeUpdateInterval.current = setInterval(() => {
          if (mainAudioRef.current) {
            setCurrentTime(mainAudioRef.current.seek());
          }
        }, 1000);
      },
      onpause: () => {
        setIsPlaying(false);
        if (timeUpdateInterval.current) {
          clearInterval(timeUpdateInterval.current);
        }
      },
      onstop: () => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (timeUpdateInterval.current) {
          clearInterval(timeUpdateInterval.current);
        }
      },
      onend: () => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (timeUpdateInterval.current) {
          clearInterval(timeUpdateInterval.current);
        }
      }
    });

    setCurrentTrack(track);
    mainAudioRef.current.play();
  }, [mainVolume, masterVolume]);

  // Toggle play/pause
  const togglePlay = useCallback(() => {
    if (!mainAudioRef.current) return;
    
    if (isPlaying) {
      mainAudioRef.current.pause();
    } else {
      mainAudioRef.current.play();
    }
  }, [isPlaying]);

  // Stop main track
  const stopTrack = useCallback(() => {
    if (mainAudioRef.current) {
      mainAudioRef.current.stop();
      setCurrentTrack(null);
    }
  }, []);

  // Seek to position
  const seekTo = useCallback((time) => {
    if (mainAudioRef.current) {
      mainAudioRef.current.seek(time);
      setCurrentTime(time);
    }
  }, []);

  // Update main volume
  const updateMainVolume = useCallback((volume) => {
    setMainVolume(volume);
    if (mainAudioRef.current) {
      mainAudioRef.current.volume(volume * masterVolume);
    }
  }, [masterVolume]);

  // Update master volume
  const updateMasterVolume = useCallback((volume) => {
    setMasterVolume(volume);
    if (mainAudioRef.current) {
      mainAudioRef.current.volume(mainVolume * volume);
    }
    // Update all ambient sounds
    Object.entries(ambientSoundsRef.current).forEach(([id, sound]) => {
      if (sound) {
        const ambientVol = ambientVolumes[id] || 0.5;
        sound.volume(ambientVol * volume);
      }
    });
  }, [mainVolume, ambientVolumes]);

  // Toggle ambient sound
  const ambientBgRef = useRef(false);
  const toggleAmbientSound = useCallback((sound) => {
    const isActive = activeAmbientSounds[sound.id];
    const count = Object.keys(activeAmbientSounds).length;
    const newCount = isActive ? count - 1 : count + 1;
    if (newCount > 0 && !ambientBgRef.current) {
      ambientBgRef.current = true;
      startBackgroundSession("Sons d'ambiance en lecture");
    } else if (newCount === 0 && ambientBgRef.current) {
      ambientBgRef.current = false;
      stopBackgroundSession();
    }
    
    if (isActive) {
      // Stop and remove
      if (ambientSoundsRef.current[sound.id]) {
        ambientSoundsRef.current[sound.id].stop();
        ambientSoundsRef.current[sound.id].unload();
        delete ambientSoundsRef.current[sound.id];
      }
      setActiveAmbientSounds(prev => {
        const newState = { ...prev };
        delete newState[sound.id];
        return newState;
      });
    } else {
      // Create and play
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const audioUrl = sound.audio_url.startsWith('http') 
        ? sound.audio_url 
        : `${backendUrl}${sound.audio_url}`;
      
      const initialVolume = ambientVolumes[sound.id] || 0.5;
      
      ambientSoundsRef.current[sound.id] = new Howl({
        src: [audioUrl],
        html5: true,
        loop: true,
        volume: initialVolume * masterVolume
      });
      
      ambientSoundsRef.current[sound.id].play();
      
      setActiveAmbientSounds(prev => ({
        ...prev,
        [sound.id]: sound
      }));
    }
  }, [activeAmbientSounds, ambientVolumes, masterVolume]);

  // Update ambient volume
  const updateAmbientVolume = useCallback((soundId, volume) => {
    setAmbientVolumes(prev => ({
      ...prev,
      [soundId]: volume
    }));
    
    if (ambientSoundsRef.current[soundId]) {
      ambientSoundsRef.current[soundId].volume(volume * masterVolume);
    }
  }, [masterVolume]);

  // Stop all ambient sounds
  const stopAllAmbient = useCallback(() => {
    Object.values(ambientSoundsRef.current).forEach(sound => {
      if (sound) {
        sound.stop();
        sound.unload();
      }
    });
    ambientSoundsRef.current = {};
    setActiveAmbientSounds({});
    if (ambientBgRef.current) {
      ambientBgRef.current = false;
      stopBackgroundSession();
    }
  }, []);

  const value = {
    // Main track
    currentTrack,
    isPlaying,
    duration,
    currentTime,
    mainVolume,
    playTrack,
    togglePlay,
    stopTrack,
    seekTo,
    updateMainVolume,
    
    // Ambient sounds
    activeAmbientSounds,
    ambientVolumes,
    toggleAmbientSound,
    updateAmbientVolume,
    stopAllAmbient,
    
    // Master
    masterVolume,
    updateMasterVolume
  };

  return (
    <ZenContext.Provider value={value}>
      {children}
    </ZenContext.Provider>
  );
};

export default ZenContext;
